import { AnimatePresence, motion } from "framer-motion";
import {
  ImageUploadArea,
  RegularSelectInput,
  TextArea,
  TextInput,
} from "components/core";

import { FC } from "react";
import { FormikProps } from "formik";
import { PromotionType } from "_graphql/__generated__/globalTypes";
import RadioSelectInput from "components/core/radio-select-input";
import _ from "lodash";

const descriptionSelect = {
  [PromotionType.SHORT_TERM]:
    "These promotions will be shown to all PlaceIT users. They will run for the time you set (using the start and end dates). The promotion will stop automatically when the time is up, but you can also end it early if you want.",
  [PromotionType.LONG_TERM]:
    "These promotions will be shown to all PlaceIT users. They will run for the time you set (using the start and end dates). The promotion will not be stopped automatically when the time is up, You will have to manual stop or end it",
};

const screenSizeRequirements = {
  [PromotionType.SHORT_TERM]: {
    small: {
      label: "Small Screen (Mobile)",
      size: "288x384",
      description: "For mobile devices and small screens",
    },
    medium: {
      label: "Medium Screen (Tablet)",
      size: "288x384",
      description: "For tablets and medium-sized screens",
    },
    large: {
      label: "Large Screen (Desktop)",
      size: "288x384",
      description: "For desktop and large screens",
    },
  },
  [PromotionType.LONG_TERM]: {
    small: {
      label: "Small Screen (Mobile)",
      size: "375x80",
      description: "For mobile devices and small screens",
    },
    medium: {
      label: "Medium Screen (Tablet)",
      size: "768x80",
      description: "For tablets and medium-sized screens",
    },
    large: {
      label: "Large Screen (Desktop)",
      size: "1920x80",
      description: "For desktop and large screens",
    },
  },
};

export interface PromotionFormProps {
  form: FormikProps<any>;
  // update?: boolean;
}

const PromotionForm: FC<PromotionFormProps> = ({ form }) => {
  return (
    <>
      <div className="space-y-6 divide-y divide-gray-200">
        <div className="grid grid-cols-2 gap-6 ">
          <div className="col-span-2">
            <RadioSelectInput
              label="Category"
              required
              id={"input.category"}
              options={[
                {
                  value: "promotion",
                  label: "Promotion",
                  description:
                    "Select promotion if you are creating a one-time offer or discount",
                },
                {
                  value: "campaign",
                  label: "Campaign",
                  description:
                    "Select campaign if you are running a broader marketing initiative",
                },
              ]}
              {...form}
            />
          </div>
          <div className="col-span-2">
            <TextInput
              id={"input.name"}
              label="Name"
              required
              placeholder="Name"
              {...form}
            />
          </div>

          <div className="col-span-2">
            <RegularSelectInput
              id={"input.type"}
              label="Type"
              options={[
                { value: PromotionType.SHORT_TERM, label: "Short Term" },
                { value: PromotionType.LONG_TERM, label: "Long Term" },
              ]}
              required
              // description={_.get(descriptionSelect, form.values.input.type, "")}
              placeholder="Type"
              {...form}
            />
          </div>
          <AnimatePresence mode="wait">
            {Boolean(form.values.input.type) && (
              <motion.div
                key={form.values.input.type}
                initial={{ opacity: 0, y: -20, height: 0 }}
                animate={{ opacity: 1, y: 0, height: "auto" }}
                exit={{ opacity: 0, y: -20, height: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="col-span-2"
              >
                <div className="bg-yellow-100 p-3 rounded-md">
                  <p className="text-sm font-medium text-yellow-700">
                    {/* the image needs to be a specific size and resolution to be accepted  and is diffrent for the type of promo  */}
                    <span className="font-light">
                      {_.get(descriptionSelect, form.values.input.type, "")}
                    </span>
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          <div className="col-span-2">
            <TextArea
              id={"input.description"}
              label="Description"
              required
              placeholder="Description"
              {...form}
            />
          </div>
          <div className="">
            <TextInput
              id={"input.start_datetime"}
              label="Start Date"
              required
              type="datetime-local"
              placeholder="Start Date"
              {...form}
            />
          </div>
          <div className="">
            <TextInput
              id={"input.end_datetime"}
              label="End Date"
              required
              type="datetime-local"
              placeholder="End Date"
              {...form}
            />
          </div>
          <AnimatePresence mode="wait">
            {Boolean(form.values.input.type) && (
              <motion.div
                key={`requirements-${form.values.input.type}`}
                initial={{ opacity: 0, y: -20, height: 0 }}
                animate={{ opacity: 1, y: 0, height: "auto" }}
                exit={{ opacity: 0, y: -20, height: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="col-span-2"
              >
                <div className="bg-blue-50 p-4 rounded-md">
                  <h3 className="text-sm font-semibold text-blue-800 mb-3">
                    Image Requirements by Screen Size:
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {Object.entries(
                      _.get(screenSizeRequirements, form.values.input.type, {})
                    ).map(([key, requirements]: [string, any], index) => (
                      <motion.div
                        key={key}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.2, delay: index * 0.1 }}
                        className="bg-white p-3 rounded border border-blue-200"
                      >
                        <h4 className="font-medium text-blue-700 text-sm">
                          {requirements.label}
                        </h4>
                        <p className="text-xs text-blue-600 mt-1">
                          {requirements.description}
                        </p>
                        <ul className="list-disc list-inside text-xs text-blue-600 mt-2">
                          <li>Size: {requirements.size} pixels</li>
                          <li>Max: 5MB</li>
                          <li>Formats: PNG, JPEG, GIF</li>
                        </ul>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Images for different screen sizes */}
          <div className="col-span-2 space-y-6">
            <h3 className="text-lg font-medium text-gray-900">
              Images for Different Screen Sizes
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="">
                <ImageUploadArea
                  id={"input.assets.small"}
                  label="Small Screen Images (Mobile)"
                  description="Upload images optimized for mobile devices"
                  required
                  accept={{ "image/*": [".png", ".jpeg", ".jpg", ".gif"] }}
                  multiple
                  {...form}
                />
              </div>

              <div className="">
                <ImageUploadArea
                  id={"input.assets.medium"}
                  label="Medium Screen Images (Tablet)"
                  description="Upload images optimized for tablets "
                  required
                  accept={{ "image/*": [".png", ".jpeg", ".jpg", ".gif"] }}
                  multiple
                  {...form}
                />
              </div>

              <div className="md:col-span-2">
                <ImageUploadArea
                  id={"input.assets.large"}
                  label="Large Screen Images (Desktop)"
                  description="Upload images optimized for desktop screens"
                  required
                  accept={{ "image/*": [".png", ".jpeg", ".jpg", ".gif"] }}
                  multiple
                  {...form}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PromotionForm;
